/**
 * `?src=`, which guide page linked a reader into the preview.
 *
 * This is the property that makes Experiment 4 readable. Without it the surface can only answer "did
 * the lab produce signups"; with it, it answers "which guides produce signups", which is the question
 * that decides where the next links go. Bifei places links of the form
 * `app.extern.com/try/consulting?src=zoetis-internship-guide`.
 *
 * Plain module, no `'use client'`: the route resolves this on the SERVER (so the first event already
 * carries it) and the client tree only reads the resolved value.
 */

/** Value when nobody linked here, a direct hit, a share, a bookmark. */
export const DIRECT_GUIDE_SOURCE = 'direct';

/** Longest `?src=` we will stamp on an event. It is untrusted input and it becomes a property, so it is bounded. */
const MAX_GUIDE_SOURCE_LENGTH = 120;

/** Slugs only. Anything with whitespace, punctuation or markup is not a page slug and is discarded. */
const SLUG_RE = /^[a-z0-9/_-]+$/;

/**
 * Reduce an untrusted `?src=` to a safe slug, or {@link DIRECT_GUIDE_SOURCE}.
 *
 * Rejecting rather than sanitising is deliberate. A half-cleaned value still enters the analytics
 * property space, where it becomes a permanent Mixpanel property value showing up in every breakdown
 * of this funnel forever. Recording a junk `src` as 'direct' is both cleaner and TRUE: a link we did
 * not place is not a guide referral. The alternative is a mangled string that splits one page's
 * numbers across several rows.
 */
/** The query parameter the guide pages carry. */
export const GUIDE_SOURCE_PARAM = 'src';

/**
 * Read and normalise `?src=` from the browser's own URL.
 *
 * 🔴 READ ON THE CLIENT, NOT THE SERVER, AND THAT IS LOAD-BEARING. Awaiting `searchParams` in the page
 * opts the whole route into dynamic rendering, and Next only enforces `dynamicParams = false` on
 * statically generated routes. So a server-side read cost two things at once: every unknown slug
 * answered 200 with the not-found page (a soft 404), and all three pages rendered per request instead
 * of being served static to cold search traffic, which is the one audience that most notices latency.
 *
 * Nothing is lost by reading it here. The value feeds analytics only, it is never rendered, and every
 * event this surface emits fires from a client effect after mount, by which point the URL is
 * available. Because it is not rendered, it also cannot cause a hydration mismatch.
 *
 * Returns {@link DIRECT_GUIDE_SOURCE} during SSR, where there is no URL to read.
 */
export function readGuideSourceFromLocation(): string {
  if (typeof window === 'undefined') {
    return DIRECT_GUIDE_SOURCE;
  }

  return normaliseGuideSource(new URLSearchParams(window.location.search).get(GUIDE_SOURCE_PARAM) ?? undefined);
}

export function normaliseGuideSource(raw: string | string[] | undefined): string {
  // Next gives an array when a param repeats (`?src=a&src=b`). First wins; there is no meaningful
  // way to attribute one visit to two pages.
  const value = Array.isArray(raw) ? raw[0] : raw;

  if (!value) {
    return DIRECT_GUIDE_SOURCE;
  }

  const cleaned = value.trim().toLowerCase().slice(0, MAX_GUIDE_SOURCE_LENGTH);

  return SLUG_RE.test(cleaned) ? cleaned : DIRECT_GUIDE_SOURCE;
}
